/**
 * Shared TanStack Query client + a localStorage persister for the boot state.
 *
 * The whole point is cold-launch responsiveness. RQ's in-memory cache is wiped
 * on every full page load, so without persistence the workspace shell blocks on
 * three network round-trips (session, identity, onboarding) before it can paint
 * — painful on iOS standalone PWAs, which the OS evicts aggressively and which
 * relaunch against a possibly-cold Durable Object. Persisting just those few
 * durable queries lets a cold launch rehydrate synchronously and render from
 * last-known state, while RQ revalidates in the background.
 */
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { QueryClient } from "@tanstack/react-query";
import type { PersistQueryClientOptions } from "@tanstack/react-query-persist-client";

/**
 * localStorage key holding the dehydrated boot cache. Exported so `signOut`
 * can purge it: identity/onboarding are user-scoped and identity is cached with
 * `staleTime: Infinity`, so a rehydrated entry would otherwise survive a
 * sign-out → sign-in-as-a-different-user on the same device.
 */
export const PERSIST_KEY = "hearth-rq-cache";

// Boot state is stable for the life of the session cookie (days). Bump the
// buster whenever the shape of any persisted query changes (or to force-evict a
// bad cached payload after a deploy).
const PERSIST_MAX_AGE = 1000 * 60 * 60 * 24 * 7; // 7 days
// 2: identity (whoami) gained a `localBuiltins` field; evict pre-field payloads
// so the client doesn't run in bucket mode off a stale persisted identity.
// 3: identity gained `relay` (the Keyhive relay target).
const PERSIST_BUSTER = "3";

// Allowlist of queries durable enough to persist. Everything else (files,
// search, chat, …) stays in-memory and refetches normally — we never want to
// rehydrate stale workspace content from disk, only the boot gate.
const BOOT_QUERY_PREFIXES: readonly (readonly unknown[])[] = [
  ["auth"], // session + identity
  ["onboarding", "state"],
  // Doc pointers the shell joins on: persisted, a warm boot goes straight to
  // IndexedDB — no network on the paint path. Their hooks' short staleTime
  // revalidates each boot, so a repointed doc (admin reseed) self-heals.
  ["project-url"],
  ["pinned-url"],
];

function isBootQuery(queryKey: readonly unknown[]): boolean {
  return BOOT_QUERY_PREFIXES.some((prefix) =>
    prefix.every((seg, i) => queryKey[i] === seg),
  );
}

/** The `useSession` query key (`["auth","session"]`), checked without importing
 * auth-client (which imports this module — would be a cycle). */
function isSessionQuery(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === "auth" && queryKey[1] === "session";
}

export const queryClient = new QueryClient();

// `storage` is null in non-browser contexts (vitest under Node imports client
// modules); the persister degrades to a no-op rather than throwing on import.
const persister = createSyncStoragePersister({
  storage: typeof window !== "undefined" ? window.localStorage : null,
  key: PERSIST_KEY,
});

export const persistOptions: Omit<PersistQueryClientOptions, "queryClient"> = {
  persister,
  maxAge: PERSIST_MAX_AGE,
  buster: PERSIST_BUSTER,
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => {
      if (query.state.status !== "success" || !isBootQuery(query.queryKey))
        return false;
      // Never persist an unauthenticated (null) session. Rehydrating it on the
      // next cold boot makes the workspace gate (`_workspace.tsx`) read a stale
      // `null`, report `isPending: false`, and bounce to /sign-in BEFORE the
      // cookie-backed revalidation resolves — the post-OAuth-redirect sign-in
      // loop. With no persisted session, the boot shows "Loading…" and waits for
      // the real fetch (which carries the freshly-set cookie). A real session
      // still persists, preserving instant cold-launch for signed-in users.
      if (isSessionQuery(query.queryKey) && query.state.data == null)
        return false;
      return true;
    },
  },
};

/** Purge persisted boot state so the next user starts from a clean slate. */
export function clearPersistedQueryCache(): void {
  if (typeof window !== "undefined")
    window.localStorage.removeItem(PERSIST_KEY);
}
